import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '@restworld/utils-common';

export type OTPInputVariant = 'primary' | 'error';

export interface OTPInputProps {
  /** Number of OTP digits (default 6) */
  numberOfDigits?: number;
  /** Called with the OTP digits as a string on every change, so a leading zero is kept ("012345") */
  onChange: (otp: string) => void;
  /** Visual variant */
  variant?: OTPInputVariant;
  /** Pre-filled OTP value as a string */
  value?: string;
  /** Whether OTP was successfully verified */
  isSuccess: boolean;
  /** Additional className for individual digit fields */
  fieldClassName?: string;
}

const generateEmptyArray = (length: number) => Array.from({ length }, () => '');

// Digits only. `isNaN(Number(...))` let a space through (`Number(' ') === 0`), which the old
// `parseInt` hid by truncating; now that the digits are passed on as typed, it has to be refused.
const DIGITS_ONLY = /^\d*$/;

export function OTPInput({
  numberOfDigits = 6,
  onChange,
  variant = 'primary',
  value,
  isSuccess = false,
  fieldClassName,
}: OTPInputProps) {
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const initialOtpDigits = useMemo(() => generateEmptyArray(numberOfDigits), [numberOfDigits]);
  const [otpDigits, setOtpDigits] = useState<string[]>(initialOtpDigits);

  useEffect(() => {
    const updated = generateEmptyArray(numberOfDigits);
    if (value && DIGITS_ONLY.test(value)) {
      value.split('').forEach((digit, index) => {
        updated[index] = digit;
      });
    }
    setOtpDigits(updated);
  }, [numberOfDigits, value]);

  const handleInputChange = useCallback(
    (index: number, val: string) => {
      if (!DIGITS_ONLY.test(val)) return;
      const updated = [...otpDigits];
      updated[index] = val;
      setOtpDigits(updated);
      if (val && index < numberOfDigits - 1) {
        inputRefs.current[index + 1]?.focus();
      }
      // A string, not a number: `parseInt('012345')` is 12345, and the parent hands that back as
      // `value`, so the boxes refilled from "12345" and the leading zero could never be typed.
      onChange(updated.join(''));
    },
    [otpDigits, numberOfDigits, onChange]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      // Codes copied from an SMS or WhatsApp often carry a trailing space or newline
      const pasteData = e.clipboardData.getData('text/plain').trim();
      if (pasteData && DIGITS_ONLY.test(pasteData)) {
        const updated = pasteData.split('').slice(0, numberOfDigits);
        setOtpDigits(updated);
        onChange(updated.join(''));
      }
    },
    [numberOfDigits, onChange]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>, index: number) => {
      if (e.key === 'Backspace') {
        const updated = [...otpDigits];
        updated[index] = '';
        setOtpDigits(updated);
        if (index > 0) {
          inputRefs.current[index - 1]?.focus();
        }
      }
    },
    [otpDigits]
  );

  const isErrored = variant === 'error';

  return (
    <div className="flex w-full flex-row justify-center gap-2">
      {otpDigits.map((digit, index) => (
        <input
          key={index}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onPaste={handlePaste}
          onKeyDown={(e) => handleKeyDown(e, index)}
          ref={(el) => {
            if (el) inputRefs.current[index] = el;
          }}
          className={cn(
            'box-border h-[59px] w-[39px] max-w-[39px] rounded-[15px] border-none bg-primary-50 text-center text-base font-normal leading-5 text-black',
            'outline-none focus:bg-white focus:ring-1 focus:ring-gray-200',
            isErrored && 'bg-red-50 ring-1 ring-red-600 placeholder:text-red-600 focus:ring-red-600',
            isSuccess && 'ring-1 ring-green-500 placeholder:text-green-500 focus:ring-green-500',
            fieldClassName
          )}
        />
      ))}
    </div>
  );
}
